import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import Browser from '@wetransfer/concorde-browser';

import Actions from '@app/store/action';
import Assets from '@app/assets';
import Helper from '@app/helper';
import I18n from '@module/i18n';
import TextField from '@component/form/element/textfield';
import Icon from '@component/icon';
import Spinner from '@component/spinner';
import Router from '@module/router';
import { WHITE } from '@app/constant/colors';
import { VALIDATION_IDENTIFIERS } from '@app/constant/validation-identifiers';
import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';
import { resendVerifyEmail } from '@app/transfer-bundle/store/actions';

function EmailVerificationResend({
  prePopulatedEmail,
  isLoggedIn,
  isVerifyingAccount,
}) {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState(prePopulatedEmail);
  const [validState, setValidState] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      setIsSubmitting(true);
      setValidState(null);
      dispatch(Actions.Notification.clearTransfer());
      if (isLoggedIn) {
        return await dispatch(
          Actions.User.resendVerifyAccountEmail({
            email,
            showTransferNotification: true,
          })
        );
      }
      await dispatch(resendVerifyEmail(email));
    } catch {
      setValidState(VALIDATION_IDENTIFIERS.NOT_VALID);
    }

    setIsSubmitting(false);
  }

  function handleBackButtonClick() {
    dispatch(Actions.Notification.clearTransfer());
    if (isLoggedIn) {
      if (isVerifyingAccount) {
        const navigateTo = Browser.isMobile ? '/upload' : '/';
        Router.navigateTo(navigateTo, false, false, { query: {} });
      }
      dispatch(Actions.Transfer.setState(TRANSFER_STATES.VERIFY_ACCOUNT));
    } else {
      dispatch(Actions.Transfer.setState(TRANSFER_STATES.VERIFY));
    }
  }

  const isSubmitButtonActive = Helper.validateEmail(email) && !isSubmitting;
  const submitButtonClassName = classnames(
    'transfer__button',
    'email-verification__submit-button',
    {
      'transfer__button--inactive': !isSubmitButtonActive,
    }
  );

  return (
    <form
      className="transfer__window email-verification"
      onSubmit={handleSubmit}
    >
      <div className="transfer__contents email-verification__contents">
        <img
          className="email-verification__lock--big"
          src={Assets.lookup('transfer_window/email-verification-lock.svg')}
          alt="Lock"
        />
        <h2 className="email-verification__title">
          {I18n.t('transfer.email-verification-resend.title')}
        </h2>
        <p className="email-verification__body email-verification__body-resend">
          {I18n.t('transfer.email-verification-resend.body')}
        </p>
        <TextField
          name="email"
          type="email"
          placeholder={I18n.t('transfer.email-verification-resend.email')}
          className="email-verification__textfield"
          inputClassName="email-verification__textfield-input"
          onChange={(event) => setEmail(event.target.value)}
          value={email}
          valid={validState}
        />
      </div>
      <div className="transfer__footer email-verification__footer">
        <Icon
          type="TransferBack"
          className="transfer__back"
          onClick={handleBackButtonClick}
          label={I18n.t('transfer.email-verification-resend.back')}
          buttonType="button"
        />
        <button
          className={submitButtonClassName}
          onClick={handleSubmit}
          disabled={!isSubmitButtonActive}
          type="submit"
        >
          {isSubmitting ? (
            <Spinner
              rotate={true}
              percentage={25}
              hideText
              circumference={24}
              stroke={3}
              className="email-verification__spinner"
              foreground={WHITE}
            />
          ) : (
            I18n.t('transfer.email-verification-resend.submit')
          )}
        </button>
      </div>
    </form>
  );
}

EmailVerificationResend.propTypes = {
  prePopulatedEmail: PropTypes.string.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  isVerifyingAccount: PropTypes.bool,
};

export default memo(EmailVerificationResend);
